import { ContentLink, EpiContent, HeaderButtonConfig } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { BlockProperty, ContentArea, HeaderIconButton, LogoutButton } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks';
import { Logout } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import { Box, Drawer, styled } from '@mui/material';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { breakpoints, palette, spacing } from '@themes/theme.helpers';
import { t } from '@translations';
import React from 'react';

// Styles
const DrawerHeader = styled(Box)`
  width: 100%;
  text-align: right;
  padding: ${spacing(2)};
  cursor: pointer;
`;

const DrawerContent = styled(Box)`
  padding: ${spacing(2)};
  width: 100%;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  height: 100%;
  ${breakpoints.up('sm')} {
    width: auto;
    min-width: 400px;
  }
  .MuiContainer-root {
    padding: 0;
  }
  .link-block {
    padding: ${spacing(2)};
    border-bottom: 1px solid ${palette.secondary('light')};
    &:hover {
      background: ${palette.secondary('light')};
    }
    a {
      color: ${palette.text('primary')};
    }
  }
`;

const LinkList = styled(Box)`
  flex-grow: 1;
`;

const LogoutWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  padding: ${spacing(2)};
`;

const StyledDrawer = styled(Drawer)`
  width: 100%;
`;

// Types
type MenuDrawerBlockProps = {
  buttonLabel: string;
  children?: React.ReactNode;
  content?: EpiContent;
  slideDirection?: 'left' | 'top' | 'right' | 'bottom';
  buttonConfig?: HeaderButtonConfig;
  contentLink?: ContentLink;
};

// Components
export const MenuDrawerBlock = ({
  slideDirection = 'right',
  buttonConfig = {},
  content = [],
  contentLink,
}: MenuDrawerBlockProps) => {
  const dispatch = useAppDispatch();
  const id = contentLink?.guidValue ?? '';
  const { isVisible } = useAppSelector(getModalState(id));
  const closeHandler = () => dispatch(setModalState({ id, isVisible: false, type: 'drawer' }));
  const openHandler = () => dispatch(setModalState({ id, isVisible: true, type: 'drawer' }));
  const container = document.querySelector('.app-global-container');
  return (
    <>
      <BlockProperty property='buttonConfig.iconName'>
        <HeaderIconButton {...buttonConfig} onClick={openHandler} sx={{ ml: 'auto' }} />
      </BlockProperty>
      <StyledDrawer
        anchor={slideDirection}
        open={isVisible}
        onClose={closeHandler}
        container={container}
      >
        <DrawerHeader onClick={closeHandler}>
          <Close sx={{ fontSize: 30 }} />
        </DrawerHeader>
        <DrawerContent>
          <LinkList>
            <BlockProperty property='content'>
              <ContentArea content={content} disableEditPanel />
            </BlockProperty>
          </LinkList>
          <LogoutWrapper>
            <LogoutButton startIcon={<Logout />} variant='secondary' onLogoutSuccess={closeHandler}>
              {t(TextCodes.Base.Logout)}
            </LogoutButton>
          </LogoutWrapper>
        </DrawerContent>
      </StyledDrawer>
    </>
  );
};
